/// <reference types="vite/client" />
import "@fontsource/geist/400.css";
import "@fontsource/geist/500.css";
import "@fontsource/geist-mono/400.css";
import "@fontsource/geist-mono/500.css";
import type { SplatViewerElement } from "../index";
import { bindShowcaseCms, syncLibrary } from "./cms";
import { fetchDemoModels, formatLibraryBytes, formatLibrarySplats, modelFromQuery, type DemoModel } from "./models";
import { bindSmoothScroll } from "./smooth-scroll";
import { showToast } from "../ui/toast";
import {
  bindWorkModeHistory,
  commitAppUrl,
  ensureWorkUrl,
  initWorkMode,
  isPubliclyVisible,
} from "./work-mode";

const modelNote = document.querySelector<HTMLElement>("#model-note");
const viewer = document.querySelector<SplatViewerElement>("splat-viewer");
const statusEl = document.querySelector<HTMLElement>("#status");
const loadLine = document.querySelector<HTMLElement>("#load-line");
const loadFill = document.querySelector<HTMLElement>("#load-line-fill");
const loadBar = document.querySelector<HTMLElement>(".load-line-bar");
const transparentBox = document.querySelector<HTMLInputElement>("#transparent");
const shell = document.querySelector<HTMLElement>(".shell");
const dock = document.querySelector<HTMLElement>(".library-dock");
const sidebarToggle = document.querySelector<HTMLButtonElement>("#sidebar-toggle");
const narrow = window.matchMedia("(max-width: 959px)");
const SIDEBAR_KEY = "scans.sidebarCollapsed";

type DockPane = "library" | "options" | "frames";
let activePane: DockPane = "library";

if (localStorage.getItem(SIDEBAR_KEY) === "1") {
  shell?.classList.add("sidebar-collapsed");
  if (sidebarToggle) {
    sidebarToggle.setAttribute("aria-label", "Expand sidebar");
    sidebarToggle.setAttribute("aria-expanded", "false");
    sidebarToggle.dataset.tip = "Expand sidebar";
  }
}

let startedAt = performance.now();
let catalog: DemoModel[] = [];
let catalogSignature = "";
let selectedId = "";
let startupModel = "";
let viewing = false;
let galleryReady = false;
let loadRequest = 0;
let cmsUser: string | null = null;

initWorkMode();
bindWorkModeHistory();

const galleryEl = document.querySelector<HTMLElement>("#gallery");
const galleryGrid = document.querySelector<HTMLElement>("#gallery-grid");
const galleryFill = document.querySelector<HTMLElement>("#gallery-boot-fill");
const galleryStatus = document.querySelector<HTMLElement>("#gallery-boot-status");
const galleryTrack = document.querySelector<HTMLElement>(".gallery-boot-track");
const galleryBootCard = document.querySelector<HTMLElement>("#gallery-boot-card");

let bootPct = 8;
const bootStarted = performance.now();
const bootTimer = window.setInterval(() => {
  if (galleryReady) {
    return;
  }
  bootPct = Math.min(86, bootPct + 6);
  paintBoot(bootPct, "Loading");
}, 160);

function paintBoot(percent: number, label: string): void {
  if (galleryFill) {
    galleryFill.style.width = `${percent}%`;
  }
  if (galleryTrack) {
    galleryTrack.setAttribute("aria-valuenow", String(percent));
  }
  if (galleryStatus) {
    galleryStatus.textContent = label;
  }
}

function revealGallery(): void {
  const wait = Math.max(0, 900 - (performance.now() - bootStarted));
  window.setTimeout(() => {
    galleryReady = true;
    window.clearInterval(bootTimer);
    paintBoot(100, "Ready");
    window.setTimeout(() => galleryEl?.classList.add("is-ready"), 380);
  }, wait);
}

function renderGallery(): void {
  if (!galleryGrid) {
    return;
  }
  galleryGrid.replaceChildren();
  const visible = catalog.filter((model) => {
    if (model.hidden) {
      return false;
    }
    if (cmsUser) {
      return true;
    }
    return isPubliclyVisible(model);
  });
  if (visible.length === 0) {
    const empty = document.createElement("p");
    empty.className = "gallery-empty";
    empty.textContent = "No scans yet.";
    galleryGrid.append(empty);
    return;
  }
  const thumb = visible.find((model) => model.thumb)?.thumb;
  if (thumb && galleryBootCard) {
    galleryBootCard.style.setProperty("--boot-image", `url("${thumb}")`);
  }
  visible.forEach((model, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "gallery-card";
    button.dataset.tip = `Open ${model.label}`;
    button.style.animationDelay = `${100 + Math.min(index, 14) * 45}ms`;
    const frame = document.createElement("span");
    frame.className = "gallery-thumb";
    if (model.thumb) {
      const img = document.createElement("img");
      img.src = model.thumb;
      img.alt = "";
      frame.append(img);
    } else {
      const initial = document.createElement("span");
      initial.className = "gallery-initial";
      initial.textContent = model.label.slice(0, 1) || "·";
      frame.append(initial);
    }
    const copy = document.createElement("span");
    copy.className = "gallery-copy";
    const label = document.createElement("span");
    label.className = "gallery-label";
    label.textContent = model.label;
    const meta = document.createElement("span");
    meta.className = "gallery-meta";
    const metaParts = [
      cmsUser && model.nsfw ? "Hidden on work link" : "",
      formatLibraryBytes(model.bytes),
      formatLibrarySplats(model.splats),
    ].filter(Boolean);
    meta.textContent = metaParts.join(" · ");
    copy.append(label, meta);
    button.append(frame, copy);
    button.addEventListener("click", () => applyModel(model.id));
    galleryGrid.append(button);
  });
}

function enterGallery(): void {
  viewing = false;
  closeAbout();
  shell?.classList.add("is-gallery");
  viewer?.pause();
  const url = new URL(window.location.href);
  url.searchParams.delete("model");
  commitAppUrl(url);
  syncInert();
}

function enterViewer(): void {
  viewing = true;
  closeAbout();
  shell?.classList.remove("is-gallery");
  viewer?.resume();
  syncInert();
}

function applyModel(id: string, force = false): void {
  const model = catalog.find((entry) => entry.id === id);
  if (!model || !viewer) {
    return;
  }
  if (!force && viewing && selectedId === id) {
    return;
  }
  if (!cmsUser && !isPubliclyVisible(model)) {
    showToast("This scan is not in the public collection.", "info");
    return;
  }
  enterViewer();
  startedAt = performance.now();
  setLoadBar(0);
  setStatus("Starting…");
  loadRequest = viewer.showModel({
    src: model.src,
    orientation: model.orientation,
    startYaw: model.startYaw ?? 0,
    startView: model.startView,
    poster: model.thumb || null,
  });
  if (modelNote) {
    modelNote.textContent = `${model.note} · orientation ${model.orientation}`;
  }
  selectedId = model.id;
  showCanvasIntro(model, model.splats, true);
  const url = new URL(window.location.href);
  url.searchParams.set("model", model.id);
  commitAppUrl(url);
  document.querySelectorAll<HTMLButtonElement>(".library-name").forEach((button) => {
    button.classList.toggle("is-on", button.dataset.id === model.id);
  });
  if (narrow.matches) closeSheet();
}

async function refreshModels(loadIfNeeded = false): Promise<void> {
  try {
    const models = await fetchDemoModels();
    const signature = models
      .map(
        (model) =>
          `${model.id}\t${model.label}\t${model.thumb}\t${model.src}\t${model.bytes}\t${model.splats}\t${model.hidden ? 1 : 0}\t${model.nsfw ? 1 : 0}\t${model.startYaw}\t${model.startView?.join(",") ?? ""}`,
      )
      .join("|");
    if (signature === catalogSignature && !loadIfNeeded) {
      syncLibrary();
      return;
    }
    catalogSignature = signature;
    catalog = models;
    if (models.length === 0 && modelNote) {
      modelNote.textContent = "Sign in and drop a .sog, or a streamed SOG folder.";
    }
    syncLibrary();
    renderGallery();
    ensureWorkUrl();
    if (!galleryReady) {
      revealGallery();
    }
    const linked = modelFromQuery(models)?.id ?? "";
    if (loadIfNeeded && linked) {
      const linkedModel = models.find((model) => model.id === linked);
      let allow = Boolean(linkedModel && (cmsUser || isPubliclyVisible(linkedModel)));
      if (linkedModel && !allow) {
        try {
          const session = await fetch("/api/session", { credentials: "include" });
          const payload = (await session.json()) as { user?: string | null };
          cmsUser = payload.user ?? null;
          allow = Boolean(cmsUser);
        } catch {
          allow = false;
        }
      }
      if (allow && linkedModel) {
        applyModel(linked);
        return;
      }
      if (linkedModel && !allow) {
        enterGallery();
        const url = new URL(window.location.href);
        url.searchParams.delete("model");
        commitAppUrl(url);
      }
    }
    if (viewing && selectedId) {
      const current = models.find((model) => model.id === selectedId);
      if (current && !cmsUser && !isPubliclyVisible(current)) {
        enterGallery();
        return;
      }
    }
    if (viewing && selectedId && models.some((model) => model.id === selectedId)) {
      return;
    }
    if (!viewing) {
      shell?.classList.add("is-gallery");
      syncInert();
    }
  } catch (error) {
    if (modelNote) {
      modelNote.textContent = error instanceof Error ? error.message : String(error);
    }
  }
}

sidebarToggle?.addEventListener("click", () => {
  const collapsed = shell?.classList.toggle("sidebar-collapsed") ?? false;
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  sidebarToggle.setAttribute("aria-label", label);
  sidebarToggle.dataset.tip = label;
  sidebarToggle.setAttribute("aria-expanded", String(!collapsed));
  try {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0");
  } catch {
    // Private mode can reject storage. The rail still toggles for this visit.
  }
});

function goToCollection(): void {
  closeSheet();
  if (shell?.classList.contains("is-about") || !shell?.classList.contains("is-gallery")) {
    enterGallery();
  }
}

document.querySelector(".sidebar-brand")?.addEventListener("click", () => {
  goToCollection();
});
document.querySelector(".sidebar-mark")?.addEventListener("click", () => {
  goToCollection();
});
document.querySelector("#nav-back")?.addEventListener("click", () => {
  goToCollection();
});
document.querySelector("#tab-back")?.addEventListener("click", () => {
  goToCollection();
});

document.querySelector("#nav-library")?.addEventListener("click", () => {
  if (shell?.classList.contains("is-gallery") && !shell.classList.contains("is-about")) {
    return;
  }
  closeAbout();
  if (narrow.matches) {
    openSheet("library");
    return;
  }
  openDesktopPane("library");
});

document.querySelector("#nav-options")?.addEventListener("click", () => {
  if (shell?.classList.contains("is-gallery") && !shell.classList.contains("is-about")) {
    return;
  }
  closeAbout();
  if (narrow.matches) {
    openSheet("options");
    return;
  }
  openDesktopPane("options");
});

document.querySelector("#nav-frames")?.addEventListener("click", () => {
  if (shell?.classList.contains("is-gallery") && !shell.classList.contains("is-about")) {
    return;
  }
  closeAbout();
  if (narrow.matches) {
    openSheet("frames");
    return;
  }
  openDesktopPane("frames");
});

document.querySelector("#nav-about")?.addEventListener("click", () => openAbout());
document.querySelector("#gallery-about")?.addEventListener("click", () => openAbout());

function openAbout(): void {
  if (shell?.classList.contains("is-about")) {
    closeAbout();
    return;
  }
  shell?.classList.add("is-about");
  if (!narrow.matches) {
    shell?.classList.add("is-collapsed");
  }
  shell?.removeAttribute("data-sheet");
  document.querySelectorAll<HTMLButtonElement>(".tabbar button").forEach((button) => {
    const on = button.dataset.sheet === "about";
    button.classList.toggle("is-on", on);
    button.setAttribute("aria-pressed", String(on));
  });
  viewer?.pause();
  syncInert();
}

function closeAbout(): void {
  if (!shell?.classList.contains("is-about")) {
    return;
  }
  shell.classList.remove("is-about");
  if (narrow.matches) {
    shell.classList.remove("is-collapsed");
  }
  document.querySelectorAll<HTMLButtonElement>(".tabbar button").forEach((button) => {
    if (button.dataset.sheet === "about") {
      button.classList.remove("is-on");
      button.setAttribute("aria-pressed", "false");
    }
  });
  if (viewing) {
    viewer?.resume();
  }
  syncInert();
}

function openDesktopPane(which: DockPane): void {
  const open = !shell?.classList.contains("is-collapsed") && activePane === which;
  if (open) {
    shell?.classList.add("is-collapsed");
    syncInert();
    return;
  }
  shell?.classList.remove("is-collapsed");
  setDockPane(which);
}

function setDockPane(which: DockPane): void {
  dock?.setAttribute("data-pane", which);
  shell?.setAttribute("data-pane", which);
  document.querySelectorAll<HTMLElement>(".dock-pane").forEach((pane) => {
    const on = pane.dataset.pane === which;
    pane.classList.remove("is-on");
    pane.toggleAttribute("inert", !on);
    if (on) {
      void pane.offsetWidth;
      pane.classList.add("is-on");
    }
  });
  activePane = which;
  if (which === "options" || which === "frames") void showTool(which);
  syncInert();
}

function syncInert(): void {
  const sheet = shell?.getAttribute("data-sheet") ?? "";
  const phone = narrow.matches;
  const gallery = shell?.classList.contains("is-gallery") ?? false;
  const collapsed = shell?.classList.contains("is-collapsed") ?? false;
  const aboutOpen = shell?.classList.contains("is-about") ?? false;
  const closed = phone ? sheet === "" || aboutOpen : collapsed || (gallery && !sheet) || aboutOpen;
  dock?.toggleAttribute("inert", closed);
  const nav = document.querySelector<HTMLElement>(".sidebar-nav");
  nav?.toggleAttribute("inert", false);
  nav?.setAttribute("aria-hidden", "false");
  document.querySelectorAll<HTMLElement>(".nav-scan").forEach((item) => {
    item.toggleAttribute("inert", gallery && !aboutOpen);
  });
  document.querySelector<HTMLElement>("#about-page")?.toggleAttribute("inert", !aboutOpen);
  const showing = !closed && !gallery && !aboutOpen;
  markNav("#nav-library", showing && activePane === "library");
  markNav("#nav-options", showing && activePane === "options");
  markNav("#nav-frames", showing && activePane === "frames");
  markNav("#nav-about", aboutOpen);
}

function markNav(selector: string, on: boolean): void {
  const button = document.querySelector<HTMLButtonElement>(selector);
  button?.classList.toggle("is-on", on);
  button?.setAttribute("aria-pressed", String(on));
}

function closeSheet(): void {
  shell?.removeAttribute("data-sheet");
  document.querySelectorAll<HTMLButtonElement>(".tabbar button").forEach((button) => {
    button.classList.remove("is-on");
    button.setAttribute("aria-pressed", "false");
  });
  syncInert();
}

function bindSheetSwipe(): void {
  const sheet = document.querySelector<HTMLElement>(".library-dock");
  const backdrop = document.querySelector<HTMLElement>(".sheet-backdrop");
  if (!sheet) {
    return;
  }
  let startX = 0;
  let startY = 0;
  let lastY = 0;
  let lastTime = 0;
  let pull = 0;
  let velocity = 0;
  let pulling = false;
  let armed = false;

  const scroller = (): HTMLElement | null =>
    sheet.querySelector(".dock-pane.is-on .dock-body, .dock-pane.is-on .tool-host");

  const atTop = (): boolean => (scroller()?.scrollTop ?? 0) <= 1;

  const release = (dismiss: boolean): void => {
    pulling = false;
    armed = false;
    sheet.classList.remove("is-pulling");
    sheet.style.removeProperty("--sheet-pull");
    backdrop?.classList.remove("is-fading");
    backdrop?.style.removeProperty("--sheet-backdrop-opacity");
    if (dismiss) {
      closeSheet();
    }
  };

  sheet.addEventListener(
    "touchstart",
    (event) => {
      if (!narrow.matches || !shell?.hasAttribute("data-sheet")) {
        return;
      }
      const touch = event.changedTouches[0];
      if (!touch || !atTop()) {
        armed = false;
        return;
      }
      startX = touch.clientX;
      startY = lastY = touch.clientY;
      lastTime = event.timeStamp;
      pull = 0;
      velocity = 0;
      pulling = false;
      armed = true;
    },
    { passive: true },
  );

  sheet.addEventListener(
    "touchmove",
    (event) => {
      if (!armed) {
        return;
      }
      const touch = event.changedTouches[0];
      if (!touch) {
        return;
      }
      const dy = touch.clientY - startY;
      const dx = touch.clientX - startX;
      if (!pulling) {
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
          armed = false;
          return;
        }
        if (dy < 10 || !atTop()) {
          return;
        }
        pulling = true;
        sheet.classList.add("is-pulling");
      }
      event.preventDefault();
      pull = Math.max(0, dy) * 0.86;
      const dt = Math.max(16, event.timeStamp - lastTime);
      velocity = (touch.clientY - lastY) / dt;
      sheet.style.setProperty("--sheet-pull", `${pull}px`);
      backdrop?.classList.add("is-fading");
      backdrop?.style.setProperty("--sheet-backdrop-opacity", String(Math.max(0, 1 - pull / 140)));
      lastY = touch.clientY;
      lastTime = event.timeStamp;
    },
    { passive: false },
  );

  const end = (): void => {
    if (!pulling) {
      armed = false;
      return;
    }
    const dismiss = pull > 110 || (pull > 42 && velocity > 0.55);
    release(dismiss);
  };

  sheet.addEventListener("touchend", end);
  sheet.addEventListener("touchcancel", () => {
    if (pulling) {
      release(false);
    }
    armed = false;
  });
}

async function showTool(which: "options" | "frames"): Promise<void> {
  const { mountSplatDebug } = await import("../debug");
  const tools = mountSplatDebug({ open: false });
  const options = document.querySelector<HTMLElement>(".svd-panel");
  const frames = document.querySelector<HTMLElement>(".svd-frames");
  const optionsHost = document.querySelector("#pane-options > .smooth-scroll-content");
  const framesHost = document.querySelector("#pane-frames > .smooth-scroll-content");
  if (options && optionsHost && options.parentElement !== optionsHost) optionsHost.append(options);
  if (frames && framesHost && frames.parentElement !== framesHost) framesHost.append(frames);
  if (options) options.hidden = false;
  tools.toggleFrames(which === "frames");
  if (which !== "frames") tools.showPanel();
  syncInert();
}

function openSheet(name: DockPane): void {
  const current = shell?.getAttribute("data-sheet");
  if (current === name) {
    closeSheet();
    return;
  }
  if (narrow.matches) {
    shell?.classList.remove("is-collapsed");
  }
  shell?.setAttribute("data-sheet", name);
  document.querySelectorAll<HTMLButtonElement>(".tabbar button").forEach((button) => {
    const on = button.dataset.sheet === name;
    button.classList.toggle("is-on", on);
    button.setAttribute("aria-pressed", String(on));
  });
  setDockPane(name);
}

narrow.addEventListener("change", syncInert);
syncInert();

document.querySelector(".sheet-backdrop")?.addEventListener("click", closeSheet);
bindSheetSwipe();
document.querySelectorAll<HTMLButtonElement>(".sheet-close").forEach((button) => {
  button.addEventListener("click", closeSheet);
});
document.querySelectorAll<HTMLButtonElement>(".tabbar button").forEach((button) => {
  button.addEventListener("click", () => {
    const name = button.dataset.sheet;
    if (name === "about") {
      openAbout();
      return;
    }
    if (name === "library" || name === "options" || name === "frames") {
      closeAbout();
      openSheet(name);
    }
  });
});
/** Saved debug settings, applied before the viewer starts so the first frame uses them. */
async function applyPageDefaults(): Promise<void> {
  if (!viewer) {
    return;
  }
  try {
    const response = await fetch("/api/defaults", { cache: "no-store", signal: AbortSignal.timeout(2500) });
    if (!response.ok) {
      return;
    }
    const payload = (await response.json()) as { settings?: Record<string, string> | null; model?: string | null };
    if (typeof payload.model === "string") {
      startupModel = payload.model;
    }
    const settings = payload.settings;
    if (!settings) {
      return;
    }
    for (const [attr, value] of Object.entries(settings)) {
      if (typeof value === "string" && value) {
        viewer.setAttribute(attr, value);
      }
    }
  } catch {
    // No saved defaults, or the page has no library API. Markup stands.
  }
}

bindSmoothScroll();

window.addEventListener("splat-cms-auth", ((event: CustomEvent<{ user?: string | null }>) => {
  cmsUser = event.detail.user ?? null;
  void refreshModels(false);
}) as EventListener);

window.addEventListener("scans-work-mode", () => {
  void refreshModels(false);
});

void (async () => {
  await applyPageDefaults();
  await import("../index");
  bindShowcaseCms({
    refreshModels,
    getCatalog: () => catalog,
    applyModel,
    getDefaultModel: () => startupModel,
    setDefaultModel: (id) => {
      startupModel = id;
    },
  });
  try {
    const session = await fetch("/api/session", { credentials: "include" });
    if (session.ok) {
      const payload = (await session.json()) as { user?: string | null };
      cmsUser = payload.user ?? null;
    }
  } catch {
    // Signed-out visitors are normal.
  }
  window.addEventListener("splat-cms-default-model", ((event: CustomEvent<{ model?: string }>) => {
    if (typeof event.detail?.model === "string") {
      startupModel = event.detail.model;
      syncLibrary();
    }
  }) as EventListener);
  void refreshModels(true);
  const { mountSplatDebug } = await import("../debug");
  mountSplatDebug({ open: false });
  if (!narrow.matches) void showTool("options");
})();

if (import.meta.hot) {
  import.meta.hot.on("splat-models-changed", () => {
    void refreshModels(false);
  });
}

function showCanvasIntro(model: DemoModel, splatCount = model.splats, replay = false): void {
  const intro = document.querySelector<HTMLElement>("#canvas-intro");
  const title = document.querySelector<HTMLElement>("#canvas-title");
  const meta = document.querySelector<HTMLElement>("#canvas-meta");
  if (title) {
    title.textContent = model.label;
  }
  if (meta) {
    const count = splatCount > 0 ? `${splatCount.toLocaleString()} splats` : "";
    const size = formatLibraryBytes(model.bytes);
    meta.textContent = [count, size].filter(Boolean).join(" · ");
  }
  if (replay && intro) {
    intro.style.animation = "none";
    void intro.offsetWidth;
    intro.style.animation = "";
  }
}

function setStatus(text: string): void {
  if (statusEl) {
    statusEl.textContent = text;
  }
}

function setLoadBar(ratio: number | null): void {
  if (!loadLine || !loadFill || !loadBar) {
    return;
  }
  if (ratio == null) {
    loadLine.hidden = true;
    return;
  }
  const pct = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  loadLine.hidden = false;
  loadFill.style.width = `${pct}%`;
  loadBar.setAttribute("aria-valuenow", String(pct));
}

viewer?.addEventListener("ready", (event) => {
  if (event.detail.generation !== loadRequest) {
    return;
  }
  const stats = event.detail;
  const model = catalog.find((entry) => entry.id === selectedId);
  if (model && stats.splatCount > 0) {
    showCanvasIntro(model, stats.splatCount);
  }
  const splats = stats.splatCount ? `${stats.splatCount.toLocaleString()} splats` : "Model ready";
  setStatus(`${splats} · ${stats.backend} · ${Math.round(performance.now() - startedAt)} ms to first frame`);
  setLoadBar(1);
  window.setTimeout(() => setLoadBar(null), 700);
});
viewer?.addEventListener("error", (event) => {
  if (event.detail.generation !== loadRequest) {
    return;
  }
  showToast(event.detail.message, "error");
  setStatus(event.detail.message);
  setLoadBar(null);
});
viewer?.addEventListener("progress", (event) => {
  if (event.detail.generation !== loadRequest) {
    return;
  }
  setStatus(event.detail.text);
  setLoadBar(event.detail.ratio);
});

transparentBox?.addEventListener("change", () => {
  viewer?.toggleAttribute("transparent", transparentBox.checked);
});

